// apps/cli/src/lib/profile-collect.ts
//
// The two questions, and the rule for who answers them.
//
// The install runbook is executed by a CODING AGENT ("You're an agent. Walk these
// in order. Pause only for `az login`, `sudo`, or a genuine choice"), so stdin is
// not a terminal on the path that matters. A readline prompt there does not ask
// anybody anything — it hangs the install. The agent is the asking surface: it puts
// both questions to the founder in chat and passes the answers as flags.
//
// That is still deterministic and agentless in the sense the ruling means. The
// questions are fixed, there is no interview, and no model decides what to collect.
// The agent is a UI — exactly what the future installer app will be, which is why
// this resolves from flags first and prompts only as a fallback: replacing the
// asking surface then is a replacement, not a refactor.
//
// Everything here is pure except `ask`, which is injected — so the prompt path is
// testable without a TTY, and the TTY-ness is a plain boolean.

import { LocalCliError } from "./errors.js";
import type { FounderIdentity } from "./founder-identity.js";
import type { StoredAdvisor, StoredOnboardingProfile } from "./onboarding-profile-store.js";

export interface ProfileFlags {
  founderEmail?: string;
  advisorName?: string;
  advisorEmail?: string;
  noAdvisor: boolean;
}

export type CollectResult =
  | { ok: true; profile: StoredOnboardingProfile }
  | { ok: false; error: LocalCliError };

/**
 * Shape only, deliberately loose — one `@`, something either side, a dot in the
 * domain, no whitespace. We are catching a typo that would mail a stranger, not
 * validating deliverability, and an over-strict pattern that rejects a real address
 * is worse than a lax one that accepts an odd-looking one.
 */
export function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export function renderEmailQuestion(prefill: string): string {
  const base = "Your email (Ezra sends your copies of its outbound mail here)";
  return prefill ? `${base} [${prefill}]: ` : `${base}: `;
}

export const ADVISOR_LEAD =
  "Your Microsoft startup advisor's name + email (Ezra emails them\n" +
  "only when you ask it to — e.g. quota requests). It's OK if you don't\n" +
  "know it yet — skip for now.\n";

/** "Enter to skip" on a first run; "Enter to keep" once there is something to keep. */
export function renderAdvisorQuestion(label: "Name " | "Email", prior: string): string {
  return prior ? `  ${label} [${prior}] (Enter to keep): ` : `  ${label} (Enter to skip): `;
}

export const ADVISOR_SKIPPED_LINE =
  "  No advisor recorded — tell Ezra any time and it'll add them.\n";

function refuseFounderEmail(): LocalCliError {
  return new LocalCliError({
    code: "PROFILE_FOUNDER_EMAIL_REQUIRED",
    message:
      "I need the founder's email address.\n" +
      "  Pass --founder-email <you@example.com> — Ezra sends their copies of its\n" +
      "  outbound mail there. Confirm it with them; don't guess from the Azure sign-in.",
    hint: "Ask the founder to confirm the address, then re-run 'm8t bootstrap profile --founder-email <addr>'.",
  });
}

function refuseMalformed(flag: string, value: string): LocalCliError {
  return new LocalCliError({
    code: "PROFILE_EMAIL_MALFORMED",
    message: `'${value}' doesn't look like an email address. Pass ${flag} <you@example.com>.`,
  });
}

/**
 * Resolve both answers: flag wins, then a prompt when we have a terminal, then refuse.
 *
 * One ask per field, deliberately. A retry loop would be more forgiving at a prompt,
 * but the command is idempotent by contract — re-running is the retry — and a loop is
 * machinery this lane does not need.
 */
export async function collectProfile(args: {
  flags: ProfileFlags;
  identity: FounderIdentity;
  isTty: boolean;
  ask: (question: string) => Promise<string>;
  now: () => string;
  /**
   * What was recorded last time, when this is a re-run.
   *
   * The command promises it is safe to re-run "if a detail was wrong". Without this,
   * correcting one answer deletes the other: `--founder-email fixed@acme.com` on a
   * second run carries no advisor flags, the advisor resolves to null, and the next
   * seed replaces a real startup advisor with the not-captured placeholder. An answer
   * you did not re-supply is one you did not change.
   */
  previous?: StoredOnboardingProfile | null;
}): Promise<CollectResult> {
  const { flags, identity, isTty, ask } = args;
  const previous = args.previous ?? null;

  // A founder who passed advisor details AND --no-advisor has contradicted themselves.
  // Picking a winner silently discards an answer they took the trouble to give.
  const hasAdvisorFlag =
    (flags.advisorName ?? "").trim() !== "" || (flags.advisorEmail ?? "").trim() !== "";
  if (flags.noAdvisor && hasAdvisorFlag) {
    return {
      ok: false,
      error: new LocalCliError({
        code: "PROFILE_ADVISOR_FLAGS_CONFLICT",
        message: "--no-advisor cannot be combined with --advisor-name / --advisor-email.",
        hint: "Drop --no-advisor to record the advisor, or drop the advisor flags to skip.",
      }),
    };
  }

  // --- the founder's own address ---
  let founderEmail = (flags.founderEmail ?? "").trim();
  if (!founderEmail) {
    // Two very different things, and conflating them defeats the whole command.
    //
    // `confirmed` is an address a human already approved on an earlier run. Reusing
    // it unattended is right — an answer not re-supplied is an answer not changed.
    //
    // `guess` is derived from the Azure sign-in. deriveEmailCandidate un-mangles a
    // guest UPN (`jane_acme.com#EXT#@host` -> `jane@acme.com`) and returns "" when it
    // cannot, so it is right often enough to OFFER and wrong often enough that it must
    // never be used unattended. It may only ever be a prompt default.
    const confirmed = previous?.founderEmail.trim() ?? "";
    const guess = identity.email.trim();
    if (!isTty) {
      if (!confirmed) return { ok: false, error: refuseFounderEmail() };
      founderEmail = confirmed;
    } else {
      const prefill = confirmed || guess;
      const answered = (await ask(renderEmailQuestion(prefill))).trim();
      founderEmail = answered || prefill;
      if (!founderEmail) return { ok: false, error: refuseFounderEmail() };
    }
  }
  if (!looksLikeEmail(founderEmail)) {
    return { ok: false, error: refuseMalformed("--founder-email", founderEmail) };
  }

  // --- their Microsoft startup advisor, if they have one ---
  let advisor: StoredAdvisor | null;
  if (flags.noAdvisor) {
    // The one way to clear a previously recorded advisor, and the reason the flag
    // still has a job on a re-run.
    advisor = null;
  } else {
    const prior = previous?.advisor ?? null;
    let name = (flags.advisorName ?? "").trim();
    let email = (flags.advisorEmail ?? "").trim();
    // Only ask when neither flag supplied anything: an agent that passed one of them
    // has already had the conversation, and asking again reads as not listening.
    if (!hasAdvisorFlag && isTty) {
      name = (await ask(ADVISOR_LEAD + renderAdvisorQuestion("Name ", prior?.name ?? ""))).trim();
      email = (await ask(renderAdvisorQuestion("Email", prior?.email ?? ""))).trim();
    }
    // An answer not given is an answer not changed — whether it was omitted from the
    // flags or entered as a bare Enter. Only --no-advisor deletes.
    name = name || (prior?.name ?? "");
    email = email || (prior?.email ?? "");
    if (email && !looksLikeEmail(email)) {
      return { ok: false, error: refuseMalformed("--advisor-email", email) };
    }
    // Both blank is the skip — in both directions. Non-interactive with no flags
    // means the runbook's founder had nothing to give, which is a skip, not a failure.
    advisor = name || email ? { name, email } : null;
  }

  return {
    ok: true,
    profile: {
      schemaVersion: 1,
      collectedAt: args.now(),
      founderName: identity.name.trim(),
      founderEmail,
      advisor,
      ...(flags.noAdvisor ? { advisorCleared: true } : {}),
    },
  };
}
